import {Box, Skeleton, Typography} from '@mui/material'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import type {FC, ReactElement} from 'react'
import {useEffect, useMemo} from 'react'
import {useCustomTranslation} from '@hooks/useCustomTranslation'
import ChatLineChart from './ChatLineChart'
import ChatBarChart from './ChatBarChart'
import ChatPieChart from './ChatPieChart'
import ChatStatTile from './ChatStatTile'
import {chartPanelSx} from './chartStyles'
import type {BarChartSpec, LineChartSpec, PieChartSpec, StatTileSpec} from './types'
import {isBarChartSpec, isLineChartSpec, isPieChartSpec, isStatTileSpec, specType} from './types'

/**
 * Renders the contents of a ```chart fenced block. The chart renders as soon as the
 * block parses to a valid spec -- even mid-stream, once the agent has emitted the
 * whole block (no need to wait for the final message). While the block is still
 * arriving (partial JSON) we show a skeleton. A parse/validation failure is only
 * surfaced once the turn is final (mid-stream failures are just incomplete input):
 * a generic user-facing message, with the reason + raw spec logged to the console.
 */

interface Props {
  raw: string
  streaming?: boolean
}

export type ParsedChart =
  | {kind: 'line'; spec: LineChartSpec}
  | {kind: 'bar'; spec: BarChartSpec}
  | {kind: 'pie'; spec: PieChartSpec}
  | {kind: 'stat'; spec: StatTileSpec}
  | {kind: 'error'; reason: string; detail?: unknown}

// Dedup console logging across re-renders/remounts of the same failed spec.
const loggedChartErrors = new Set<string>()

/** Parse + validate a ```chart block's body. Shared with the report view (AB#6490),
 *  which draws the same specs but decides for itself which kinds it supports. */
export function parseChart(raw: string): ParsedChart {
  let value: unknown
  try {
    value = JSON.parse(raw.trim())
  } catch (e) {
    return {kind: 'error', reason: 'invalid JSON', detail: e}
  }
  if (isLineChartSpec(value)) return {kind: 'line', spec: value}
  if (isBarChartSpec(value)) return {kind: 'bar', spec: value}
  if (isPieChartSpec(value)) return {kind: 'pie', spec: value}
  if (isStatTileSpec(value)) return {kind: 'stat', spec: value}
  const t = specType(value)
  return {kind: 'error', reason: t ? `malformed "${t}" chart spec` : 'unrecognised chart spec'}
}

/** Loading state shown while the agent is still emitting the chart block. */
const ChartSkeleton: FC = () => (
  <Box sx={chartPanelSx}>
    <Skeleton variant="text" width="45%" height={22} />
    <Skeleton variant="rounded" width="100%" height={180} sx={{mt: 0.5, borderRadius: 1}} />
  </Box>
)

/** Generic user-facing failure. The reason + raw spec go to the console, not here. */
const ChartError: FC = () => {
  const [t] = useCustomTranslation('alpinist')
  return (
    <Box
      sx={{
        my: 1.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1.5,
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 1,
        color: 'text.secondary',
      }}
    >
      <WarningAmberRoundedIcon fontSize="small" />
      <Typography variant="caption">{t('chart_render_error')}</Typography>
    </Box>
  )
}

const ChartBlock: FC<Props> = ({raw, streaming = false}): ReactElement => {
  // Always try to parse -- the chart renders the moment the block is complete + valid,
  // even while the rest of the turn is still streaming.
  const parsed = useMemo<ParsedChart>(() => parseChart(raw), [raw])

  useEffect(() => {
    // A failure mid-stream just means the block is still arriving; only report a real
    // failure once the turn is final.
    if (!streaming && parsed.kind === 'error' && !loggedChartErrors.has(raw)) {
      loggedChartErrors.add(raw)
      // eslint-disable-next-line no-console
      console.error(`[Alpinist] could not render chart (${parsed.reason})`, {
        raw,
        detail: parsed.detail,
      })
    }
  }, [parsed, raw, streaming])

  switch (parsed.kind) {
    case 'line':
      return <ChatLineChart spec={parsed.spec} />
    case 'bar':
      return <ChatBarChart spec={parsed.spec} />
    case 'pie':
      return <ChatPieChart spec={parsed.spec} />
    case 'stat':
      return <ChatStatTile spec={parsed.spec} />
    default:
      // Not parseable yet: still streaming -> skeleton; final -> user-facing error.
      return streaming ? <ChartSkeleton /> : <ChartError />
  }
}

export default ChartBlock
